import type {
  DecideResponse,
  HealthResponse,
  PendingResponse,
  PendingTx,
  SignPendingResponse,
  ZerionPortfolio,
  ZerionPositionsPage,
  ZerionTransactionsPage,
} from './types.js'

export interface SafewardClientConfig {
  baseUrl: string
  authToken: string
}

/**
 * Module-level dedup + short response cache for read-only Zerion-backed
 * endpoints (portfolio, transactions). Solves three real-world rate-limit
 * causes:
 *   1. React StrictMode runs each useEffect twice on mount in dev → two
 *      identical fetches in ~ms. With dedup, the second piggybacks on the
 *      first promise.
 *   2. Vite HMR re-mounts the dashboard on every file save → a 60s response
 *      cache means HMR cycles within a minute reuse the previous response.
 *   3. /v1/transactions has no worker-side cache (unlike /v1/portfolio's KV
 *      cache); Zerion's per-second rate limit is the only ceiling, so any
 *      burst tips it.
 *
 * `pending`/`health` aren't cached here — they're polled deliberately and
 * shouldn't be coalesced.
 */
const cachedResponses = new Map<
  string,
  { value: unknown; expiresAt: number }
>()
const inflight = new Map<string, Promise<unknown>>()
const CACHE_TTL_MS = 60_000

/** Test seam — drops both the response cache and any in-flight promises. */
export function clearSafewardCache(): void {
  cachedResponses.clear()
  inflight.clear()
}

/**
 * Cheap deterministic fingerprint of the auth token for cache keying. We
 * don't need cryptographic strength — just "different tokens produce
 * different keys" so a rotation invalidates the cache. djb2 hash of the
 * token (including empty string), fits in a short hex string.
 */
function tokenFingerprint(token: string): string {
  let h = 5381
  for (let i = 0; i < token.length; i++) {
    h = (h * 33) ^ token.charCodeAt(i)
  }
  // >>> 0 forces unsigned for a clean hex representation
  return (h >>> 0).toString(16)
}

/**
 * Carries the HTTP status code as a structured field so callers can branch
 * on `err.status === 401` instead of substring-matching the message — which
 * mis-fires when the request URL itself contains the digits (e.g. a Safe
 * address with "401" in its hex). See usePending.
 */
export class SafewardHttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly path: string,
    body: string,
  ) {
    super(`${path} ${status}: ${body}`)
    this.name = 'SafewardHttpError'
  }
}

export class SafewardClient {
  constructor(private readonly config: SafewardClientConfig) {}

  async health(): Promise<HealthResponse> {
    const resp = await fetch(`${this.config.baseUrl}/v1/health`)
    if (!resp.ok)
      throw new SafewardHttpError(resp.status, '/v1/health', await resp.text())
    return (await resp.json()) as HealthResponse
  }

  async pending(safe?: string): Promise<PendingResponse> {
    const q = safe ? `?safe=${safe}` : ''
    return this.get<PendingResponse>(`/v1/pending${q}`)
  }

  async decide(tx: PendingTx): Promise<DecideResponse> {
    return this.post<DecideResponse>('/v1/decide', { safe: tx.safe, ...txAsBody(tx) })
  }

  async signPending(safeTxHash: string, safe?: string): Promise<SignPendingResponse> {
    const body: Record<string, unknown> = { safeTxHash }
    if (safe) body.safe = safe
    return this.post<SignPendingResponse>('/v1/sign-pending', body)
  }

  /**
   * Zerion-backed portfolio snapshot for an address. Worker caches in KV
   * with ~1h TTL; pass `refresh: true` to bust.
   */
  async portfolio(
    address: string,
    opts: {
      currency?: string
      positions?: 'only_simple' | 'only_complex' | 'no_filter'
      refresh?: boolean
    } = {},
  ): Promise<ZerionPortfolio> {
    const q = new URLSearchParams({ address })
    if (opts.currency) q.set('currency', opts.currency)
    if (opts.positions) q.set('positions', opts.positions)
    if (opts.refresh) q.set('refresh', '1')
    return this.getCached<ZerionPortfolio>(`/v1/portfolio?${q.toString()}`)
  }

  /**
   * Paginated tx history for an address. Defaults to descending mined_at;
   * use `pageAfter` from `nextCursor(prevPage)` to fetch the next page.
   */
  async transactions(
    address: string,
    opts: {
      currency?: string
      chainIds?: string
      operationTypes?: string
      pageSize?: number
      pageAfter?: string
      sort?: '-mined_at' | 'mined_at'
      trash?: 'only_non_trash' | 'only_trash' | 'no_filter'
      searchQuery?: string
    } = {},
  ): Promise<ZerionTransactionsPage> {
    const q = new URLSearchParams({ address })
    if (opts.currency) q.set('currency', opts.currency)
    if (opts.chainIds) q.set('chain_ids', opts.chainIds)
    if (opts.operationTypes) q.set('operation_types', opts.operationTypes)
    if (opts.pageSize) q.set('page_size', String(opts.pageSize))
    if (opts.pageAfter) q.set('page_after', opts.pageAfter)
    if (opts.sort) q.set('sort', opts.sort)
    if (opts.trash) q.set('trash', opts.trash)
    if (opts.searchQuery) q.set('search_query', opts.searchQuery)
    return this.getCached<ZerionTransactionsPage>(
      `/v1/transactions?${q.toString()}`,
    )
  }

  /**
   * Per-token holdings. Worker KV-caches at ~1h; pass `refresh: true` to
   * bust. Server-side defaults: trash=only_non_trash, sort=-value.
   */
  async positions(
    address: string,
    opts: {
      currency?: string
      chainIds?: string
      positions?: 'only_simple' | 'only_complex' | 'no_filter'
      trash?: 'only_non_trash' | 'only_trash' | 'no_filter'
      sort?: '-value' | 'value' | '-name' | 'name'
      refresh?: boolean
      /** Decorate each position with its DefiLlama match (apy, defillamaUrl).
       *  Worker pays the same cost as a plain positions call (matching is
       *  done in-memory against a 24h-cached DefiLlama snapshot). */
      includeYields?: boolean
    } = {},
  ): Promise<ZerionPositionsPage> {
    const q = new URLSearchParams({ address })
    if (opts.currency) q.set('currency', opts.currency)
    if (opts.chainIds) q.set('chain_ids', opts.chainIds)
    if (opts.positions) q.set('positions', opts.positions)
    if (opts.trash) q.set('trash', opts.trash)
    if (opts.sort) q.set('sort', opts.sort)
    if (opts.refresh) q.set('refresh', '1')
    if (opts.includeYields) q.set('include', 'yields')
    return this.getCached<ZerionPositionsPage>(`/v1/positions?${q.toString()}`)
  }

  /**
   * Off-chain EIP-1271 co-sign. Sends the inner hash + the original dApp
   * request so the worker can run the smart-permit policy before signing.
   * Returns Guardian's 65-byte SafeMessage sig over the Module Safe's
   * domain when the policy approves; throws on refusal.
   */
  async signMessage(args: {
    innerHash: string
    request:
      | { method: 'personal_sign'; message: string }
      | { method: 'eth_signTypedData_v4'; payload: unknown }
  }): Promise<{
    signed: boolean
    signature: string
    signerAddress: string
    moduleSafe: string
    decision: { passed: boolean; detail: string }
  }> {
    return this.post('/v1/sign-message', args)
  }

  /**
   * Like `get`, but module-level deduplicates concurrent identical requests
   * and serves recent (<60s) successful responses from cache. Used only for
   * Zerion-proxy endpoints. Cache key mixes baseUrl + a hash of the auth
   * token so a token rotation within the session doesn't serve the
   * previous tenant's cached response.
   */
  private async getCached<T>(path: string): Promise<T> {
    const url = `${this.config.baseUrl}${path}`
    const cacheKey = `${tokenFingerprint(this.config.authToken)}|${url}`
    const now = Date.now()
    const cached = cachedResponses.get(cacheKey)
    if (cached && cached.expiresAt > now) {
      return cached.value as T
    }
    const existing = inflight.get(cacheKey)
    if (existing) return existing as Promise<T>
    const p = (async () => {
      try {
        const value = await this.get<T>(path)
        cachedResponses.set(cacheKey, {
          value,
          expiresAt: Date.now() + CACHE_TTL_MS,
        })
        return value
      } finally {
        inflight.delete(cacheKey)
      }
    })()
    inflight.set(cacheKey, p)
    return p as Promise<T>
  }

  private async get<T>(path: string): Promise<T> {
    const resp = await fetch(`${this.config.baseUrl}${path}`, {
      headers: { Authorization: `Bearer ${this.config.authToken}` },
    })
    if (!resp.ok)
      throw new SafewardHttpError(resp.status, `GET ${path}`, await resp.text())
    return (await resp.json()) as T
  }

  private async post<T>(path: string, body: unknown): Promise<T> {
    const resp = await fetch(`${this.config.baseUrl}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.config.authToken}`,
      },
      body: JSON.stringify(body),
    })
    if (!resp.ok)
      throw new SafewardHttpError(resp.status, `POST ${path}`, await resp.text())
    return (await resp.json()) as T
  }
}

/**
 * Pull the opaque `page[after]` cursor out of a Zerion `links.next` URL.
 * The next URL Zerion returns is fully-qualified; we just need the cursor
 * piece to feed back into `client.transactions(addr, { pageAfter })`.
 */
export function nextCursor(page: ZerionTransactionsPage): string | null {
  if (!page.links.next) return null
  try {
    return new URL(page.links.next).searchParams.get('page[after]')
  } catch {
    return null
  }
}

function txAsBody(tx: PendingTx): Record<string, unknown> {
  return {
    to: tx.to,
    value: tx.value,
    data: tx.data,
    operation: tx.operation,
    safeTxGas: tx.safeTxGas,
    baseGas: tx.baseGas,
    gasPrice: tx.gasPrice,
    gasToken: tx.gasToken,
    refundReceiver: tx.refundReceiver,
    nonce: tx.nonce,
    safeTxHash: tx.safeTxHash,
  }
}
